import { lazy } from "react";
import { createBrowserRouter } from "react-router";
import {
  AuthProvider,
  FeatureRouteGuard,
  HomeRedirect,
  withAuthGuard,
} from "@/auth";
import { StudioFeatures } from "@intelliextract-studio/shared";
import { DashboardLayout } from "@/presentation/components/layout";
import { WebAppConfig } from "@/infrastructure/services/config/AppConfig";
import { Sentry } from "@/infrastructure/services/logger";

const DashboardPage = lazy(() => import("@/presentation/pages/DashboardPage"));
const RequestsPage = lazy(() => import("@/presentation/pages/RequestsPage"));
const UsagePage = lazy(() => import("@/presentation/pages/UsagePage"));
const StrangerFilesPage = lazy(() => import("@/presentation/pages/StrangerFilesPage"));
const ConsumersPage = lazy(() => import("@/presentation/pages/ConsumersPage"));
const AccessDeniedPage = lazy(() => import("@/presentation/pages/AccessDeniedPage"));
const LoginPage = lazy(() => import("@/presentation/pages/LoginPage"));
const AuthCallbackPage = lazy(() => import("@/presentation/pages/AuthCallbackPage"));

const ProtectedDashboardLayout = WebAppConfig.getInstance().isAuthConfigured()
  ? withAuthGuard(DashboardLayout)
  : DashboardLayout;

const SentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const appRouter = SentryCreateBrowserRouter([
  {
    element: <AuthProvider />,
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/auth/callback",
        element: <AuthCallbackPage />,
      },
      {
        path: "/",
        element: <ProtectedDashboardLayout />,
        children: [
          { index: true, element: <HomeRedirect /> },
          {
            path: "access-denied",
            element: <AccessDeniedPage />,
          },
          {
            path: "dashboard",
            element: (
              <FeatureRouteGuard feature={StudioFeatures.DASHBOARD}>
                <DashboardPage />
              </FeatureRouteGuard>
            ),
          },
          {
            path: "requests",
            element: (
              <FeatureRouteGuard feature={StudioFeatures.REQUESTS}>
                <RequestsPage />
              </FeatureRouteGuard>
            ),
          },
          {
            path: "usage",
            element: (
              <FeatureRouteGuard feature={StudioFeatures.USAGE}>
                <UsagePage />
              </FeatureRouteGuard>
            ),
          },
          {
            path: "stranger-files",
            element: (
              <FeatureRouteGuard feature={StudioFeatures.STRANGER_FILES}>
                <StrangerFilesPage />
              </FeatureRouteGuard>
            ),
          },
          {
            path: "consumers",
            element: (
              <FeatureRouteGuard feature={StudioFeatures.CONSUMERS}>
                <ConsumersPage />
              </FeatureRouteGuard>
            ),
          },
        ],
      },
    ],
  },
]);
